/**
 * ActivityTypeFilter Component
 *
 * Tab-based filter for activity types. Allows filtering the timeline
 * by activity type (All, Calls, Emails, Meetings, Notes, Tasks).
 *
 * Requirements: Task 8 - Activity Timeline Page
 */

import { Tabs, TabsList, TabsTrigger } from '@bloomcode/ui';
import { ActivityType } from '@bloomcode/graphql';

interface ActivityTypeFilterProps {
  activeFilter: ActivityType[] | undefined;
  onFilterChange: (filter: ActivityType[] | undefined) => void;
}

/**
 * Filter tab definitions mapping labels to ActivityType values
 */
const filterTabs: Array<{ label: string; value: ActivityType[] | undefined }> = [
  { label: 'All', value: undefined },
  { label: 'Calls', value: [ActivityType.CALL] },
  { label: 'Emails', value: [ActivityType.EMAIL] },
  { label: 'Meetings', value: [ActivityType.MEETING] },
  { label: 'Notes', value: [ActivityType.NOTE] },
  { label: 'Tasks', value: [ActivityType.TASK] },
];

/**
 * Determines the active tab key based on the current filter
 */
function getActiveTabKey(filter: ActivityType[] | undefined): string {
  if (!filter || filter.length === 0) return 'all';
  return filter[0].toLowerCase();
}

export function ActivityTypeFilter({ activeFilter, onFilterChange }: ActivityTypeFilterProps) {
  const activeKey = getActiveTabKey(activeFilter);

  return (
    <Tabs
      value={activeKey}
      onValueChange={(value) => {
        const tab = filterTabs.find((t) => (t.value ? t.value[0].toLowerCase() : 'all') === value);
        onFilterChange(tab?.value);
      }}
      aria-label="Filter activities by type"
    >
      <TabsList>
        {filterTabs.map((tab) => {
          const key = tab.value ? tab.value[0].toLowerCase() : 'all';
          return (
            <TabsTrigger key={key} value={key}>
              {tab.label}
            </TabsTrigger>
          );
        })}
      </TabsList>
    </Tabs>
  );
}

export type { ActivityTypeFilterProps };
export default ActivityTypeFilter;
